import React, { useEffect, useState } from 'react';
import {
  DataList,
  DataListCell,
  DataListItemCells,
  Switch,
} from '@patternfly/react-core';
import { DragDropSort, DraggableObject } from '@patternfly/react-drag-drop';
import { GROUPING_LEVEL_LABELS, GroupingConfig, GroupingLevel, MAX_ENABLED_GROUPING_LEVELS, ReportGroupingConfig } from '../../types/reports';

interface ReportsGroupingConfigProps {
  config: ReportGroupingConfig;
  onChange: (config: ReportGroupingConfig) => void;
}

export const ReportsGroupingConfig: React.FC<ReportsGroupingConfigProps> = ({ config, onChange }) => {
  const sortedLevels = [...config.levels].sort((a, b) => a.order - b.order);
  const enabledCount = config.levels.filter((l) => l.enabled).length;

  const buildItems = (levels: GroupingConfig[]): DraggableObject[] => {
    return levels.map((levelConfig) => {
      const isLastEnabled = levelConfig.enabled && enabledCount <= 1;
      const isMaxReached = !levelConfig.enabled && enabledCount >= MAX_ENABLED_GROUPING_LEVELS;
      const label = GROUPING_LEVEL_LABELS[levelConfig.level];

      return {
        id: levelConfig.level,
        content: (
          <DataListItemCells
            dataListCells={[
              <DataListCell key={levelConfig.level} isFilled>
                <Switch
                  id={`grouping-switch-${levelConfig.level}`}
                  label={label}
                  isChecked={levelConfig.enabled}
                  onChange={() => {
                    if (isLastEnabled || isMaxReached) return;
                    const newLevels = config.levels.map((l) =>
                      l.level === levelConfig.level ? { ...l, enabled: !l.enabled } : l
                    );
                    onChange({ levels: newLevels });
                  }}
                  isDisabled={isLastEnabled || isMaxReached}
                />
              </DataListCell>,
            ]}
          />
        ),
      };
    });
  };

  const [items, setItems] = useState<DraggableObject[]>(buildItems(sortedLevels));

  useEffect(() => {
    setItems(buildItems(sortedLevels));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const handleDrop = (_: unknown, newItems: DraggableObject[]) => {
    setItems(newItems);
    const newLevels: GroupingConfig[] = newItems.map((item, index) => {
      const level = item.id as GroupingLevel;
      const existingLevel = config.levels.find((l) => l.level === level)!;
      return {
        ...existingLevel,
        order: index,
      };
    });
    onChange({ levels: newLevels });
  };

  return (
    <div className="reports-grouping-config">
      <div className="reports-grouping-config__title">Group by</div>
      <div className="reports-grouping-config__description">
        Drag to reorder. Toggle to enable/disable. Max {MAX_ENABLED_GROUPING_LEVELS} active.
      </div>
      <DragDropSort
        items={items}
        onDrop={handleDrop}
        variant="DataList"
        overlayProps={{ isCompact: true }}
      >
        <DataList aria-label="Grouping levels" isCompact />
      </DragDropSort>
      <div className="reports-grouping-config__preview">
        <strong>Active:</strong>{' '}
        {sortedLevels
          .filter((l) => l.enabled)
          .map((l) => GROUPING_LEVEL_LABELS[l.level])
          .join(' → ') || 'None'}
      </div>
    </div>
  );
};
